import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { captureUtm } from "./lib/utm";
import { AuthProvider } from "./context/AuthContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { Toaster } from "./components/ui/sonner";
import Landing from "./pages/Landing";
import Auth from "./pages/Auth";
import AuthCallback from "./pages/AuthCallback";
import Dashboard from "./pages/Dashboard";
import AcademyView from "./pages/AcademyView";
import ModuleView from "./pages/ModuleView";
import QuizView from "./pages/QuizView";
import Instructor from "./pages/Instructor";
import Achievements from "./pages/Achievements";
import Profile from "./pages/Profile";
import Checkout from "./pages/Checkout";
import Admin from "./pages/Admin";
import AdminLeads from "./pages/AdminLeads";
import AdminUsers from "./pages/AdminUsers";
import Checklist from "./pages/Checklist";
import SeoLanding from "./pages/SeoLanding";
import Legal from "./pages/Legal";
import ThankYou from "./pages/ThankYou";
import WelcomePilot from "./pages/WelcomePilot";
import FaaActionPlan from "./pages/FaaActionPlan";
import CommandCenter from "./pages/CommandCenter";
import ReactivationCenter from "./pages/ReactivationCenter";
import { ConsentGate } from "./components/ConsentGate";
import Maintenance from "./pages/Maintenance";
import OperatorSignIn from "./pages/OperatorSignIn";
import { SiteStatusProvider, useSiteStatus, AS } from "./lib/siteStatus";
import { useAuth } from "./context/AuthContext";

const OPERATOR_PATH = "/operator-sign-in";

function AppRoutes() {
  const location = useLocation();
  const { maintenance, loaded } = useSiteStatus();
  const { user, ready } = useAuth();
  if (loaded && maintenance && location.hash?.includes("session_id=")) return <Maintenance />;
  if (!loaded || (maintenance && !ready))
    return <div data-testid="site-loading" style={{ minHeight: "100vh", background: AS.bg }} />;
  const m = maintenance;
  if (m && user?.role !== "admin")
    return location.pathname === OPERATOR_PATH ? <OperatorSignIn /> : <Maintenance />;
  const toAdmin = <Navigate to="/admin" replace />;
  // Detect the OAuth session_id in the URL fragment during render (before other routes run)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={m ? toAdmin : <Landing />} />
      <Route path="/learn/:slug" element={m ? toAdmin : <SeoLanding />} />
      <Route path="/login" element={m ? toAdmin : <Auth mode="login" />} />
      <Route path={OPERATOR_PATH} element={toAdmin} />
      <Route path="/register" element={m ? toAdmin : <Auth mode="register" />} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/welcome" element={<ProtectedRoute><WelcomePilot /></ProtectedRoute>} />
      <Route path="/academy/:slug" element={<ProtectedRoute><AcademyView /></ProtectedRoute>} />
      <Route path="/module/:moduleId" element={<ProtectedRoute><ModuleView /></ProtectedRoute>} />
      <Route path="/quiz/:moduleId" element={<ProtectedRoute><QuizView /></ProtectedRoute>} />
      <Route path="/instructor" element={m ? toAdmin : <ProtectedRoute><Instructor /></ProtectedRoute>} />
      <Route path="/achievements" element={<ProtectedRoute><Achievements /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
      <Route path="/checkout" element={m ? toAdmin : <ProtectedRoute><Checkout /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
      <Route path="/admin/command-center" element={<ProtectedRoute><CommandCenter /></ProtectedRoute>} />
      <Route path="/admin/reactivation" element={<ProtectedRoute><ReactivationCenter /></ProtectedRoute>} />
      <Route path="/admin/leads" element={<ProtectedRoute><AdminLeads /></ProtectedRoute>} />
      <Route path="/admin/users" element={<ProtectedRoute><AdminUsers /></ProtectedRoute>} />
      <Route path="/faa-action-plan" element={<ProtectedRoute><FaaActionPlan /></ProtectedRoute>} />
      <Route path="/part-107-checklist" element={<Checklist />} />
      <Route path="/thank-you" element={m ? toAdmin : <ThankYou />} />
      <Route path="/legal/:docKey" element={<Legal />} />
      <Route path="*" element={m ? toAdmin : <Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  useEffect(() => {
    captureUtm();
  }, []);
  return (
    <div className="App">
      <HelmetProvider>
        <AuthProvider>
          <SiteStatusProvider>
            <BrowserRouter>
              <AppRoutes />
              <ConsentGate />
            </BrowserRouter>
          </SiteStatusProvider>
          <Toaster position="top-right" richColors theme="dark" />
        </AuthProvider>
      </HelmetProvider>
    </div>
  );
}

export default App;
